/*
 * deco.js — 4Winners "no blank garments" rules.
 *  1. <deco-picker> on blank products: decoration method + locations, logo upload,
 *     required mockup (drag/resize logo on the product photo), in-hands deadline.
 *  2. FWDeco.request(el, items): adds garments + per-unit decoration fee line
 *     (+ one-time rush fee) in a single multipart /cart/add.js call.
 *  3. Cart guard: blocks checkout while any blank item is undecorated or a design
 *     is under the minimum, and keeps fee quantities in sync with garment quantities.
 */
(function () {
  'use strict';
  if (window.FWDeco) return;

  var CFG = window.FWDecoConfig || {};
  var ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  /* ---------- helpers ---------- */
  function money(cents) { return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
  function today() { var d = new Date(); d.setHours(0, 0, 0, 0); return d; }
  function parseDate(s) { var p = String(s).split('-'); return new Date(+p[0], +p[1] - 1, +p[2]); }
  function iso(d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); }
  function bizDaysUntil(s) {
    var t = parseDate(s), d = today(), n = 0;
    while (d < t) { d.setDate(d.getDate() + 1); var w = d.getDay(); if (w !== 0 && w !== 6) n++; }
    return n;
  }
  function fmtDate(s) { return parseDate(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  function getJSON(url) { return fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(function (r) { return r.json(); }); }
  function postJSON(url, body) {
    return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })
      .then(function (r) { return r.json().then(function (d) { if (!r.ok) throw new Error(d.description || 'Cart update failed'); return d; }); });
  }
  function loadImg(src) {
    return new Promise(function (res, rej) {
      var i = new Image(); i.crossOrigin = 'anonymous';
      i.onload = function () { res(i); }; i.onerror = function () { rej(new Error('Image failed to load')); };
      i.src = src;
    });
  }
  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }

  /* Default logo placement per location: centre x/y and width, as fractions of the photo. */
  var POS = {
    'Front': { x: 0.5, y: 0.38, w: 0.3 },
    'Back': { x: 0.5, y: 0.34, w: 0.36 },
    'Left Chest': { x: 0.62, y: 0.31, w: 0.13 },
    'Right Chest': { x: 0.38, y: 0.31, w: 0.13 },
    'Left Sleeve': { x: 0.83, y: 0.36, w: 0.09 },
    'Right Sleeve': { x: 0.17, y: 0.36, w: 0.09 }
  };
  var HAT_POS = { x: 0.5, y: 0.42, w: 0.3 };
  var BAG_POS = { x: 0.5, y: 0.5, w: 0.4 };

  /* ---------- the picker ---------- */
  class DecoPicker extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      var ds = this.dataset;
      this.method = ds.method;
      this.feeVariant = +ds.feeVariant;
      this.feePrice = +ds.feePrice;
      this.rushVariant = +ds.rushVariant;
      this.rushPrice = +ds.rushPrice;
      this.rushDays = +ds.rushDays || 10;
      this.min = +ds.min || 25;
      this.isBag = ds.bag === 'true';
      try { this.data = JSON.parse(this.querySelector('[data-deco-data]').textContent); } catch (e) { this.data = { images: [], variants: [] }; }

      this.fileIn = this.querySelector('[data-deco-file]');
      this.vecIn = this.querySelector('[data-deco-vector]');
      this.dateIn = this.querySelector('[data-deco-deadline]');
      this.errEl = this.querySelector('[data-deco-err]');
      this.sumEl = this.querySelector('[data-deco-summary]');
      this.rushEl = this.querySelector('[data-deco-rush]');
      this.mockBtn = this.querySelector('[data-deco-mockup-btn]');
      this.mockPrev = this.querySelector('[data-deco-mockup-preview]');
      this.mockStatus = this.querySelector('[data-deco-mockup-status]');
      this.placement = {};
      this.mockup = null;

      var t = today(); t.setDate(t.getDate() + 1);
      if (this.dateIn) this.dateIn.min = iso(t);

      var self = this;
      this.addEventListener('change', function (e) {
        if (e.target.matches('[data-deco-loc]') || e.target === self.fileIn) {
          if (e.target === self.fileIn) self.checkFile();
          self.invalidate();
        }
        if (e.target === self.dateIn) self.update();
      });
      if (this.mockBtn) this.mockBtn.addEventListener('click', function () { self.openDesigner(); });

      /* Recalculate when sizes/colours change in the same product block. */
      var root = this.root();
      root.addEventListener('input', function (e) { if (e.target.matches('[data-srg-input], input[name="quantity"]')) self.update(); });
      root.addEventListener('srg:change', function () { self.update(); });
      root.addEventListener('change', function (e) {
        if (!self.contains(e.target)) { setTimeout(function () { self.invalidate(); }, 50); }
      });
      this.update();
    }

    root() { return this.closest('.main_product_info_inner') || this.closest('.quick_add_drawer_inner') || document; }
    grid() { return this.root().querySelector('size-run-grid'); }

    locations() {
      if (this.method === 'embroidery') return ['Front'];
      return Array.prototype.slice.call(this.querySelectorAll('[data-deco-loc]:checked')).map(function (i) { return i.value; });
    }

    units() {
      var g = this.grid();
      if (g && typeof g.totals === 'function') return g.totals().units;
      var q = this.root().querySelector('product-form input[name="quantity"], .main_product_quantity_wrapper input[name="quantity"]');
      return q ? (parseInt(q.value, 10) || 0) : 0;
    }

    color() {
      var g = this.grid();
      if (g && typeof g.currentGroup === 'function') { var c = g.currentGroup(); if (c) return String(c); }
      var picked = this.root().querySelector('.main_product_variant_picker_wrapper input[type="radio"]:checked, .main_product_variant_picker_wrapper select');
      return picked ? String(picked.value) : '';
    }

    checkFile() {
      var f = this.fileIn && this.fileIn.files[0];
      if (!f) return;
      if (!/^image\/(png|jpe?g|svg\+xml|webp)$/i.test(f.type)) {
        this.fileIn.value = '';
        this.showErr('Upload your logo as PNG, JPG or SVG so we can place it on the mockup. Vector files (AI, EPS, PDF) can be added in the optional field.');
      } else if (f.size > 20 * 1024 * 1024) {
        this.fileIn.value = '';
        this.showErr('Logo file is over 20 MB. Please upload a smaller file.');
      } else {
        this.showErr('');
      }
    }

    key() {
      var f = this.fileIn && this.fileIn.files[0];
      return [this.locations().join(','), f ? f.name + f.size + f.lastModified : '', this.color()].join('|');
    }

    invalidate() {
      if (this.mockup && this.mockup.key !== this.key()) {
        this.mockup = null;
        if (this.mockPrev) { this.mockPrev.hidden = true; this.mockPrev.innerHTML = ''; }
        if (this.mockStatus) this.mockStatus.textContent = 'Your selections changed. Create your mockup again.';
      }
      this.update();
    }

    state() {
      var locs = this.locations();
      var f = this.fileIn && this.fileIn.files[0];
      var date = this.dateIn && this.dateIn.value;
      var units = this.units();
      if (!locs.length) return { ok: false, msg: 'Choose at least one print location.' };
      if (!f) return { ok: false, msg: 'Upload your logo to continue.' };
      if (!this.mockup) return { ok: false, msg: 'Create and save your mockup before adding to cart.' };
      if (!date) return { ok: false, msg: 'Choose your in-hands deadline.' };
      if (parseDate(date) <= today()) return { ok: false, msg: 'Your in-hands deadline must be a future date.' };
      if (units < this.min) return { ok: false, msg: 'Minimum order is ' + this.min + ' units per design. We do not sell blank garments.' };
      var bd = bizDaysUntil(date);
      return { ok: true, locs: locs, file: f, date: date, units: units, biz: bd, rush: bd < this.rushDays };
    }

    showErr(msg) {
      if (!this.errEl) return;
      this.errEl.textContent = msg || '';
      this.errEl.hidden = !msg;
    }

    update() {
      var locs = this.locations(), units = this.units();
      var per = this.feePrice * locs.length;
      if (this.sumEl) {
        if (locs.length && units > 0) {
          this.sumEl.innerHTML = '<span>Decoration: ' + units + ' units × ' + (this.method === 'embroidery' ? 'embroidery' : locs.length + (locs.length === 1 ? ' location' : ' locations')) +
            '</span><strong>' + money(per * units) + '</strong>';
        } else {
          this.sumEl.innerHTML = '<span>Decoration is added per unit, on top of the garment price.</span>';
        }
      }
      if (this.rushEl && this.dateIn) {
        var d = this.dateIn.value;
        if (d && parseDate(d) > today()) {
          var bd = bizDaysUntil(d);
          if (bd < this.rushDays) {
            this.rushEl.textContent = 'Rush order: ' + fmtDate(d) + ' is ' + bd + ' business ' + (bd === 1 ? 'day' : 'days') + ' away. A one-time ' + money(this.rushPrice) + ' rush fee will be added.';
            this.rushEl.hidden = false;
          } else { this.rushEl.hidden = true; }
        } else { this.rushEl.hidden = true; }
      }
      if (this.mockBtn) this.mockBtn.disabled = !(locs.length && this.fileIn && this.fileIn.files[0]);
      if (this.errEl && !this.errEl.hidden && this.state().ok) this.showErr('');
    }

    /* ----- product photos ----- */
    frontImage() {
      var c = this.color().toLowerCase();
      var vs = this.data.variants || [];
      for (var i = 0; i < vs.length; i++) {
        var v = vs[i];
        if (v.img && [v.o1, v.o2, v.o3].some(function (o) { return o && String(o).toLowerCase() === c; })) return v.img;
      }
      return this.data.featured || (this.data.images[0] && this.data.images[0].src) || '';
    }
    backImage() {
      var c = this.color().toLowerCase().replace(/[^a-z0-9]+/g, '');
      var imgs = this.data.images || [];
      var backs = imgs.filter(function (im) { return /back/i.test((im.alt || '') + ' ' + im.src); });
      var withColor = backs.filter(function (im) { return ((im.alt || '') + im.src).toLowerCase().replace(/[^a-z0-9]+/g, '').indexOf(c) !== -1; });
      return (withColor[0] || backs[0] || {}).src || '';
    }
    imageFor(loc) {
      if (loc === 'Back') { var b = this.backImage(); if (b) return { src: b, back: true }; return { src: this.frontImage(), back: false, label: true }; }
      return { src: this.frontImage(), back: false };
    }
    defaultPos(loc) {
      if (this.method === 'embroidery') return Object.assign({}, HAT_POS);
      if (this.isBag) return Object.assign({}, BAG_POS);
      return Object.assign({}, POS[loc] || POS.Front);
    }

    /* ----- mockup designer ----- */
    openDesigner() {
      var self = this, locs = this.locations(), f = this.fileIn.files[0];
      if (!locs.length || !f) { this.showErr(!locs.length ? 'Choose at least one print location.' : 'Upload your logo first.'); return; }
      if (this.logoUrl) URL.revokeObjectURL(this.logoUrl);
      this.logoUrl = URL.createObjectURL(f);
      locs.forEach(function (l) { if (!self.placement[l]) self.placement[l] = self.defaultPos(l); });

      var dlg = document.createElement('dialog');
      dlg.className = 'deco-dlg';
      dlg.innerHTML =
        '<div class="deco-dlg__panel">' +
          '<div class="deco-dlg__top"><h3>Create your mockup</h3><button type="button" class="deco-dlg__x" data-x aria-label="Close">×</button></div>' +
          '<p class="deco-dlg__sub">Drag your logo into place and size it. This shows us your intended design. We send a professional proof for approval before production.</p>' +
          '<div class="deco-dlg__tabs" role="tablist"></div>' +
          '<div class="deco-dlg__stage" data-stage><img class="deco-dlg__base" alt="" data-base><img class="deco-dlg__logo" alt="Your logo" data-logo draggable="false"><span class="deco-dlg__flag" data-flag hidden>Back view</span></div>' +
          '<label class="deco-dlg__size">Logo size <input type="range" min="4" max="70" step="1" data-size></label>' +
          '<div class="deco-dlg__actions"><button type="button" class="deco-dlg__reset" data-reset>Reset position</button><button type="button" class="btn deco-dlg__save" data-save>Save mockup</button></div>' +
          '<p class="deco-dlg__err" data-derr hidden></p>' +
        '</div>';
      document.body.appendChild(dlg);

      var tabs = dlg.querySelector('.deco-dlg__tabs'), stage = dlg.querySelector('[data-stage]'),
          base = dlg.querySelector('[data-base]'), logo = dlg.querySelector('[data-logo]'),
          size = dlg.querySelector('[data-size]'), flag = dlg.querySelector('[data-flag]'), derr = dlg.querySelector('[data-derr]');
      var active = locs[0];
      logo.src = this.logoUrl;

      function place() {
        var p = self.placement[active];
        logo.style.left = (p.x * 100) + '%';
        logo.style.top = (p.y * 100) + '%';
        logo.style.width = (p.w * 100) + '%';
        size.value = Math.round(p.w * 100);
      }
      function show(loc) {
        active = loc;
        Array.prototype.forEach.call(tabs.children, function (b) { b.setAttribute('aria-selected', b.dataset.loc === loc ? 'true' : 'false'); });
        var im = self.imageFor(loc);
        base.src = im.src;
        flag.hidden = !(loc === 'Back' && im.label);
        place();
      }
      locs.forEach(function (l) {
        var b = document.createElement('button');
        b.type = 'button'; b.role = 'tab'; b.className = 'deco-dlg__tab'; b.dataset.loc = l; b.textContent = self.method === 'embroidery' ? 'Front (embroidery)' : l;
        b.addEventListener('click', function () { show(l); });
        tabs.appendChild(b);
      });
      tabs.hidden = locs.length < 2;

      /* drag */
      var drag = null;
      logo.addEventListener('pointerdown', function (e) {
        e.preventDefault();
        var r = stage.getBoundingClientRect();
        drag = { id: e.pointerId, r: r, dx: e.clientX - (self.placement[active].x * r.width + r.left), dy: e.clientY - (self.placement[active].y * r.height + r.top) };
        logo.setPointerCapture(e.pointerId);
        logo.classList.add('is-drag');
      });
      logo.addEventListener('pointermove', function (e) {
        if (!drag || e.pointerId !== drag.id) return;
        var p = self.placement[active];
        p.x = clamp((e.clientX - drag.dx - drag.r.left) / drag.r.width, 0.03, 0.97);
        p.y = clamp((e.clientY - drag.dy - drag.r.top) / drag.r.height, 0.03, 0.97);
        place();
      });
      function endDrag() { drag = null; logo.classList.remove('is-drag'); }
      logo.addEventListener('pointerup', endDrag);
      logo.addEventListener('pointercancel', endDrag);
      size.addEventListener('input', function () { self.placement[active].w = (+size.value) / 100; place(); });
      dlg.querySelector('[data-reset]').addEventListener('click', function () { self.placement[active] = self.defaultPos(active); place(); });

      function close() { dlg.classList.remove('is-open'); setTimeout(function () { dlg.close(); dlg.remove(); }, 200); }
      dlg.querySelector('[data-x]').addEventListener('click', close);
      dlg.addEventListener('cancel', function (e) { e.preventDefault(); close(); });
      dlg.addEventListener('click', function (e) { if (e.target === dlg) close(); });

      dlg.querySelector('[data-save]').addEventListener('click', function () {
        var btn = this; btn.disabled = true; btn.textContent = 'Saving…'; derr.hidden = true;
        self.render(locs).then(function (out) {
          self.mockup = { file: out.file, url: out.url, key: self.key(), placement: JSON.parse(JSON.stringify(self.placement)) };
          if (self.mockPrev) { self.mockPrev.innerHTML = '<img alt="Your saved mockup" src="' + out.url + '">'; self.mockPrev.hidden = false; }
          if (self.mockStatus) self.mockStatus.textContent = 'Mockup saved. You can edit it any time before adding to cart.';
          if (self.mockBtn) self.mockBtn.textContent = 'Edit mockup';
          self.update();
          close();
        }).catch(function (err) {
          derr.textContent = 'Could not save the mockup (' + err.message + '). Please try again.';
          derr.hidden = false; btn.disabled = false; btn.textContent = 'Save mockup';
        });
      });

      dlg.showModal();
      requestAnimationFrame(function () { dlg.classList.add('is-open'); });
      show(active);
    }

    /* Composite every location into one PNG, side by side, with labels. */
    render(locs) {
      var self = this;
      return Promise.all([loadImg(this.logoUrl)].concat(locs.map(function (l) { return loadImg(self.imageFor(l).src); })))
        .then(function (imgs) {
          var lg = imgs[0], W = 900, pad = 24, labelH = 56;
          var panels = locs.map(function (l, i) { var b = imgs[i + 1]; return { loc: l, img: b, h: Math.round(W * b.naturalHeight / b.naturalWidth) }; });
          var H = Math.max.apply(null, panels.map(function (p) { return p.h; })) + labelH;
          var cv = document.createElement('canvas');
          cv.width = panels.length * W + (panels.length + 1) * pad; cv.height = H + pad * 2 + 40;
          var cx = cv.getContext('2d');
          cx.fillStyle = '#ffffff'; cx.fillRect(0, 0, cv.width, cv.height);
          panels.forEach(function (p, i) {
            var ox = pad + i * (W + pad), oy = pad;
            cx.drawImage(p.img, ox, oy + labelH, W, p.h);
            var pl = self.placement[p.loc], lw = pl.w * W, lh = lw * lg.naturalHeight / lg.naturalWidth;
            cx.drawImage(lg, ox + pl.x * W - lw / 2, oy + labelH + pl.y * p.h - lh / 2, lw, lh);
            cx.fillStyle = '#111'; cx.font = '600 30px system-ui, -apple-system, Segoe UI, sans-serif';
            cx.fillText((self.method === 'embroidery' ? 'Embroidery, ' : 'DTF, ') + p.loc, ox, oy + 36);
          });
          cx.fillStyle = '#666'; cx.font = '22px system-ui, -apple-system, Segoe UI, sans-serif';
          cx.fillText(self.dataset.productTitle + (self.color() ? ' · ' + self.color() : '') + ' · Customer mockup, not a production proof', pad, cv.height - 22);
          return new Promise(function (res, rej) {
            cv.toBlob(function (b) {
              if (!b) return rej(new Error('image export blocked'));
              var file = new File([b], 'mockup-' + Date.now() + '.png', { type: 'image/png' });
              res({ file: file, url: URL.createObjectURL(b) });
            }, 'image/png');
          });
        });
    }

    /* ----- add to cart ----- */
    submit(items) {
      var s = this.state(), self = this;
      if (!s.ok) { this.showErr(s.msg); this.scrollIntoView({ behavior: 'smooth', block: 'center' }); return Promise.reject(new Error(s.msg)); }
      this.showErr('');
      var id = 'D' + Date.now().toString(36).toUpperCase();
      var methodLabel = this.method === 'embroidery' ? 'Embroidery' : 'DTF';
      var label = methodLabel + ': ' + s.locs.join(', ');
      var dl = fmtDate(s.date) + (s.rush ? ' (rush)' : '');
      var color = this.color();
      var total = items.reduce(function (a, b) { return a + (+b.quantity); }, 0);
      var feeKey = null;
      return getJSON(ROOT + 'cart.js').then(function (cart) {
        var hasRush = cart.items.some(function (i) { return i.variant_id === self.rushVariant; });
        /* Step 1: decoration fee line, carrying the logo + mockup files (file uploads need a single-item multipart add). */
        var fd = new FormData();
        fd.append('id', self.feeVariant);
        fd.append('quantity', total * s.locs.length);
        var props = {
          'For': self.dataset.productTitle + (color ? ' / ' + color : ''),
          'Locations': s.locs.join(', '),
          'Units': String(total),
          'In-hands deadline': dl,
          '_placement': JSON.stringify(self.mockup.placement),
          '_deco_id': id,
          '_deco_fee': '1'
        };
        Object.keys(props).forEach(function (k) { fd.append('properties[' + k + ']', props[k]); });
        fd.append('properties[Logo]', s.file, s.file.name);
        fd.append('properties[Mockup]', self.mockup.file, self.mockup.file.name);
        if (self.vecIn && self.vecIn.files[0]) fd.append('properties[Vector file]', self.vecIn.files[0], self.vecIn.files[0].name);
        return fetch(ROOT + 'cart/add.js', { method: 'POST', body: fd, headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json().then(function (d) { if (!r.ok) throw new Error(d.description || 'Could not upload your logo. Please try again.'); feeKey = d.key; return hasRush; }); });
      }).then(function (hasRush) {
        /* Step 2: garments (+ one-time rush fee). */
        var lines = items.map(function (it) {
          return { id: +it.id, quantity: +it.quantity, properties: { 'Decoration': label, 'In-hands deadline': dl, '_deco_id': id, '_deco_locs': String(s.locs.length), '_rush': s.rush ? '1' : '0' } };
        });
        if (s.rush && !hasRush) lines.push({ id: self.rushVariant, quantity: 1, properties: { 'In-hands deadline': dl, '_rush_fee': '1' } });
        return fetch(ROOT + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: lines }) })
          .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
          .then(function (res) {
            if (!res.ok && feeKey) return postJSON(ROOT + 'cart/change.js', { id: feeKey, quantity: 0 }).then(function () { return res; }, function () { return res; });
            return res;
          });
      });
    }
  }
  customElements.define('deco-picker', DecoPicker);

  function pickerFor(el) {
    var root = el && (el.closest('.main_product_info_inner') || el.closest('.quick_add_drawer_inner'));
    return root ? root.querySelector('deco-picker') : null;
  }

  /* Standard product form (products without a Size option). */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
    var p = pickerFor(form);
    if (!p) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var idIn = form.querySelector('[name="id"]'), qIn = form.querySelector('[name="quantity"]') || p.root().querySelector('input[name="quantity"]');
    var items = [{ id: idIn.value, quantity: Math.max(1, parseInt(qIn && qIn.value, 10) || 1) }];
    var errEl = form.querySelector('.product_add_error');
    p.submit(items).then(function (res) {
      if (!res.ok) throw new Error((res.data && res.data.description) || 'Could not add to cart.');
      window.location.href = ROOT + 'cart';
    }).catch(function (err) {
      if (errEl) { errEl.textContent = err.message; errEl.classList.remove('hidden'); }
    });
  }, true);

  /* ---------- cart guard ---------- */
  var Guard = {
    busy: null,
    ok: false,
    inspect: function () {
      return Promise.all([getJSON(ROOT + 'cart.js'), fetch(ROOT + 'cart?view=deco', { credentials: 'same-origin' }).then(function (r) { return r.text(); }).then(function (t) { return JSON.parse(t); })]).then(function (r) {
        var cart = r[0], meta = {}, rushV = +CFG.rushVariant || 0;
        (r[1].items || []).forEach(function (m) { meta[m.key] = m; });
        var missing = [], short = [], need = {}, units = {}, mins = {}, titles = {}, fees = {}, rush = [], rushNeeded = false, hasBlank = false;
        cart.items.forEach(function (it) {
          var pr = it.properties || {}, m = meta[it.key] || {};
          if (it.variant_id === rushV || pr._rush_fee) { rush.push(it); return; }
          if (it.handle === 'decoration-fee' || pr._deco_fee) { var k = pr._deco_id || '_none'; (fees[k] = fees[k] || []).push(it); return; }
          if (!m.req) return;
          hasBlank = true;
          if (!pr._deco_id) { missing.push({ title: it.product_title, url: m.url }); return; }
          var id = pr._deco_id;
          need[id] = (need[id] || 0) + it.quantity * (parseInt(pr._deco_locs, 10) || 1);
          units[id] = (units[id] || 0) + it.quantity;
          mins[id] = m.min || 25;
          titles[id] = it.product_title;
          if (pr._rush === '1') rushNeeded = true;
        });
        var changes = [];
        Object.keys(fees).forEach(function (id) {
          fees[id].forEach(function (f, i) { var want = i === 0 ? (need[id] || 0) : 0; if (f.quantity !== want) changes.push({ id: f.key, quantity: want }); });
        });
        Object.keys(need).forEach(function (id) {
          if (!fees[id]) missing.push({ title: titles[id] + ' (decoration was removed)', url: null });
          else if (units[id] < mins[id]) short.push({ title: titles[id], units: units[id], min: mins[id] });
        });
        var addRush = false;
        if (rushNeeded) {
          if (!rush.length && rushV) addRush = true;
          rush.forEach(function (x, i) { var want = i === 0 ? 1 : 0; if (x.quantity !== want) changes.push({ id: x.key, quantity: want }); });
        } else {
          rush.forEach(function (x) { changes.push({ id: x.key, quantity: 0 }); });
        }
        return { missing: missing, short: short, changes: changes, addRush: addRush, hasBlank: hasBlank };
      });
    },
    run: function () {
      if (Guard.busy) return Guard.busy;
      Guard.busy = Guard.inspect().then(function (res) {
        if (!res.changes.length && !res.addRush) return res;
        var chain = Promise.resolve();
        res.changes.forEach(function (c) { chain = chain.then(function () { return postJSON(ROOT + 'cart/change.js', c); }); });
        if (res.addRush) chain = chain.then(function () { return postJSON(ROOT + 'cart/add.js', { items: [{ id: +CFG.rushVariant, quantity: 1, properties: { _rush_fee: '1' } }] }); });
        return chain.then(function () {
          if (/\/cart\/?$/.test(location.pathname)) { location.reload(); return new Promise(function () {}); }
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
          return Guard.inspect();
        });
      }).then(function (res) { Guard.paint(res); return res; })
        .finally(function () { Guard.busy = null; });
      return Guard.busy;
    },
    paint: function (res) {
      var blocked = res.missing.length > 0 || res.short.length > 0;
      Guard.ok = !blocked;
      document.documentElement.classList.toggle('deco-blocked', blocked);
      document.documentElement.classList.toggle('deco-ok', !blocked);
      var hosts = document.querySelectorAll('.main_cart__items_wrapper, .cart_drawer_items_footer');
      Array.prototype.forEach.call(hosts, function (host) {
        var box = host.parentNode.querySelector(':scope > .deco-cart-alert');
        if (!res.hasBlank && !blocked) { if (box) box.remove(); return; }
        if (!box) { box = document.createElement('div'); box.className = 'deco-cart-alert'; box.setAttribute('role', 'status'); host.parentNode.insertBefore(box, host); }
        var html = '<p class="deco-cart-alert__rule"><strong>Minimum order 25 units per design.</strong> We do not sell blank garments. Every blank item is decorated with your logo.</p>';
        if (blocked) {
          html += '<p class="deco-cart-alert__err">Checkout is paused until these are fixed:</p><ul>';
          res.missing.forEach(function (m) { html += '<li>' + m.title + ' needs decoration. Remove it, then add it again from the ' + (m.url ? '<a href="' + m.url + '">product page</a>' : 'product page') + ' with your logo and mockup.</li>'; });
          res.short.forEach(function (s) { html += '<li>' + s.title + ': ' + s.units + ' units. Add ' + (s.min - s.units) + ' more to reach the ' + s.min + '-unit minimum.</li>'; });
          html += '</ul>';
        }
        box.innerHTML = html;
        box.classList.toggle('is-blocked', blocked);
      });
    }
  };

  function goingToCheckout(el) {
    if (!el) return false;
    if (el.matches('button[name="checkout"], input[name="checkout"]')) return true;
    var a = el.closest('a[href]');
    return !!(a && /\/checkout(\/|\?|$)/.test(a.getAttribute('href')));
  }
  document.addEventListener('click', function (e) {
    var t = e.target.closest('a, button, input');
    if (!goingToCheckout(t)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    Guard.run().then(function (res) {
      if (res.missing.length || res.short.length) {
        var box = document.querySelector('.deco-cart-alert.is-blocked');
        if (box) box.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        window.location.href = ROOT + 'checkout';
      }
    });
  }, true);

  /* Re-check when the cart changes (page, drawer, quick add). */
  var timer;
  function soon() { clearTimeout(timer); timer = setTimeout(Guard.run, 350); }
  document.addEventListener('cart:refresh', soon);
  document.addEventListener('cart:build', soon);
  document.addEventListener('DOMContentLoaded', function () {
    if (/\/cart\/?$/.test(location.pathname)) Guard.run();
    var drawer = document.querySelector('cart-contents');
    if (drawer && window.MutationObserver) {
      new MutationObserver(function (muts) {
        if (Guard.busy) return;
        if (muts.some(function (m) { return !(m.target.closest && m.target.closest('.deco-cart-alert')); })) soon();
      }).observe(drawer, { childList: true, subtree: true });
    }
    document.addEventListener('click', function (e) { if (e.target.closest('[data-drawer="cart_drawer"], .cart_icon, a[href$="/cart"]')) soon(); });
  });

  window.FWDeco = {
    request: function (el, items) { var p = pickerFor(el); return p ? p.submit(items) : null; },
    guard: Guard
  };
})();
